import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";

interface SmithChartProps {
  z0?: number; // Reference impedance (default 50Ω)
  onPointChange?: (point: SmithPoint) => void;
  initialPoint?: { r: number; x: number };
  enableSnapping?: boolean;
}

export interface SmithPoint {
  // Normalized impedance
  r: number; // Normalized resistance (R/Z0)
  x: number; // Normalized reactance (X/Z0)
  // Actual impedance
  resistance: number; // R in ohms
  reactance: number; // X in ohms
  // Reflection coefficient
  gamma: number; // |Γ|
  gammaAngle: number; // ∠Γ in degrees
  gammaReal: number; // Re(Γ)
  gammaImag: number; // Im(Γ)
  // VSWR
  vswr: number;
}

// SVG viewBox size (chart will scale to fill container)
const VIEWBOX_SIZE = 660;
const CHART_RADIUS = 260;
const CENTER = VIEWBOX_SIZE / 2;

// Resistance circle values - standard textbook density
const R_CIRCLES_MAJOR = [0, 0.5, 1, 2];
const R_CIRCLES_MEDIUM = [0.1, 0.2, 0.3, 0.4, 0.6, 0.8, 1.5, 3, 4, 5, 10];

// Reactance arc values - standard textbook density
const X_ARCS_MAJOR = [0.5, 1, 2];
const X_ARCS_MEDIUM = [0.1, 0.2, 0.3, 0.4, 0.6, 0.8, 1.5, 3, 4, 5, 10];

// Values for snapping
const SNAP_R_VALUES = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.8, 1, 1.5, 2, 3, 4, 5, 10];
const SNAP_X_VALUES = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.8, 1, 1.5, 2, 3, 4, 5, 10];
const SNAP_THRESHOLD = 0.06;

export function SmithChart({ z0 = 50, onPointChange, initialPoint, enableSnapping = true }: SmithChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [point, setPoint] = useState<SmithPoint | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Convert normalized impedance to reflection coefficient
  const impedanceToGamma = useCallback((r: number, x: number) => {
    const denomReal = r + 1;
    const denomImag = x;
    const denomMagSq = denomReal * denomReal + denomImag * denomImag;

    const numReal = r - 1;
    const numImag = x;

    const gammaReal = (numReal * denomReal + numImag * denomImag) / denomMagSq;
    const gammaImag = (numImag * denomReal - numReal * denomImag) / denomMagSq;

    return { gammaReal, gammaImag };
  }, []);

  // Convert reflection coefficient to normalized impedance
  const gammaToImpedance = useCallback((gammaReal: number, gammaImag: number) => {
    const denomReal = 1 - gammaReal;
    const denomImag = -gammaImag;
    const denomMagSq = denomReal * denomReal + denomImag * denomImag;

    if (denomMagSq < 0.0001) {
      return { r: Infinity, x: 0 };
    }

    const numReal = 1 + gammaReal;
    const numImag = gammaImag;

    const r = (numReal * denomReal + numImag * denomImag) / denomMagSq;
    const x = (numImag * denomReal - numReal * denomImag) / denomMagSq;

    return { r: Math.max(0, r), x };
  }, []);

  // Convert chart coordinates to gamma
  const chartToGamma = useCallback((chartX: number, chartY: number) => {
    const gammaReal = (chartX - CENTER) / CHART_RADIUS;
    const gammaImag = -(chartY - CENTER) / CHART_RADIUS;
    return { gammaReal, gammaImag };
  }, []);

  // Convert gamma to chart coordinates
  const gammaToChart = useCallback((gammaReal: number, gammaImag: number) => {
    const chartX = CENTER + gammaReal * CHART_RADIUS;
    const chartY = CENTER - gammaImag * CHART_RADIUS;
    return { chartX, chartY };
  }, []);

  // Snap to nearest grid line
  const snapToGrid = useCallback((r: number, x: number): { r: number; x: number } => {
    if (!enableSnapping) return { r, x };

    let snappedR = r;
    let snappedX = x;

    let minRDist = Infinity;
    for (const snapR of SNAP_R_VALUES) {
      const dist = Math.abs(r - snapR);
      if (dist < minRDist && dist < SNAP_THRESHOLD * (1 + r)) {
        minRDist = dist;
        snappedR = snapR;
      }
    }

    let minXDist = Infinity;
    for (const snapX of SNAP_X_VALUES) {
      const distPos = Math.abs(x - snapX);
      if (distPos < minXDist && distPos < SNAP_THRESHOLD * (1 + Math.abs(x))) {
        minXDist = distPos;
        snappedX = snapX;
      }
      if (snapX !== 0) {
        const distNeg = Math.abs(x - (-snapX));
        if (distNeg < minXDist && distNeg < SNAP_THRESHOLD * (1 + Math.abs(x))) {
          minXDist = distNeg;
          snappedX = -snapX;
        }
      }
    }

    return { r: snappedR, x: snappedX };
  }, [enableSnapping]);

  // Calculate full point data from normalized impedance
  const calculatePoint = useCallback((r: number, x: number): SmithPoint => {
    const { gammaReal, gammaImag } = impedanceToGamma(r, x);
    const gamma = Math.sqrt(gammaReal * gammaReal + gammaImag * gammaImag);
    const gammaAngle = Math.atan2(gammaImag, gammaReal) * (180 / Math.PI);
    const vswr = gamma < 0.9999 ? (1 + gamma) / (1 - gamma) : Infinity;

    return {
      r,
      x,
      resistance: r * z0,
      reactance: x * z0,
      gamma,
      gammaAngle,
      gammaReal,
      gammaImag,
      vswr,
    };
  }, [z0, impedanceToGamma]);

  // Handle click/drag on chart
  const handleChartInteraction = useCallback((event: MouseEvent | React.MouseEvent) => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const chartX = ((event.clientX - rect.left) / rect.width) * VIEWBOX_SIZE;
    const chartY = ((event.clientY - rect.top) / rect.height) * VIEWBOX_SIZE;

    let { gammaReal, gammaImag } = chartToGamma(chartX, chartY);
    const gammaMag = Math.sqrt(gammaReal * gammaReal + gammaImag * gammaImag);

    if (gammaMag > 0.99) {
      const scale = 0.99 / gammaMag;
      gammaReal *= scale;
      gammaImag *= scale;
    }

    let { r, x } = gammaToImpedance(gammaReal, gammaImag);
    const snapped = snapToGrid(r, x);
    r = snapped.r;
    x = snapped.x;

    const newPoint = calculatePoint(r, x);
    setPoint(newPoint);
    onPointChange?.(newPoint);
  }, [chartToGamma, gammaToImpedance, calculatePoint, onPointChange, snapToGrid]);

  // Set initial point
  useEffect(() => {
    if (initialPoint) {
      const newPoint = calculatePoint(initialPoint.r, initialPoint.x);
      setPoint(newPoint);
    }
  }, [initialPoint, calculatePoint]);

  // Draw the Smith Chart
  useEffect(() => {
    if (!svgRef.current) return;

    // Defers drawing to next tick to allow parent animations to start smoothly
    const timer = setTimeout(() => {
      if (!svgRef.current) return;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      // Background
      svg.append("rect")
        .attr("width", VIEWBOX_SIZE)
        .attr("height", VIEWBOX_SIZE)
        .attr("fill", "#fefefe");

      const g = svg.append("g");

      // Clip path for the unit circle
      svg.append("defs")
        .append("clipPath")
        .attr("id", "smith-clip")
        .append("circle")
        .attr("cx", CENTER)
        .attr("cy", CENTER)
        .attr("r", CHART_RADIUS);

      const chartGroup = g.append("g").attr("clip-path", "url(#smith-clip)");

      // Helper to draw resistance circle
      const drawResistanceCircle = (r: number, stroke: string, strokeWidth: number) => {
        if (r === 0) return;

        const centerX = CENTER + (r / (r + 1)) * CHART_RADIUS;
        const radius = (1 / (r + 1)) * CHART_RADIUS;

        chartGroup.append("circle")
          .attr("cx", centerX)
          .attr("cy", CENTER)
          .attr("r", radius)
          .attr("fill", "none")
          .attr("stroke", stroke)
          .attr("stroke-width", strokeWidth);
      };

      // Helper to draw reactance arc
      const drawReactanceArc = (x: number, stroke: string, strokeWidth: number) => {
        const arcRadius = (1 / Math.abs(x)) * CHART_RADIUS;

        // Positive X (inductive)
        chartGroup.append("circle")
          .attr("cx", CENTER + CHART_RADIUS)
          .attr("cy", CENTER - arcRadius)
          .attr("r", arcRadius)
          .attr("fill", "none")
          .attr("stroke", stroke)
          .attr("stroke-width", strokeWidth);

        // Negative X (capacitive)
        chartGroup.append("circle")
          .attr("cx", CENTER + CHART_RADIUS)
          .attr("cy", CENTER + arcRadius)
          .attr("r", arcRadius)
          .attr("fill", "none")
          .attr("stroke", stroke)
          .attr("stroke-width", strokeWidth);
      };

      // Draw impedance grid
      // Medium grid first
      R_CIRCLES_MEDIUM.forEach(r => drawResistanceCircle(r, "#ccc", 0.5));
      X_ARCS_MEDIUM.forEach(x => drawReactanceArc(x, "#ccc", 0.5));

      // Major grid on top
      R_CIRCLES_MAJOR.forEach(r => drawResistanceCircle(r, "#888", 1));
      X_ARCS_MAJOR.forEach(x => drawReactanceArc(x, "#888", 1));

      // Unity circle (r=1) emphasized
      drawResistanceCircle(1, "#555", 1.5);

      // Draw center horizontal line (X = 0)
      chartGroup.append("line")
        .attr("x1", CENTER - CHART_RADIUS)
        .attr("y1", CENTER)
        .attr("x2", CENTER + CHART_RADIUS)
        .attr("y2", CENTER)
        .attr("stroke", "#444")
        .attr("stroke-width", 1.5);

      // Draw unit circle outline
      g.append("circle")
        .attr("cx", CENTER)
        .attr("cy", CENTER)
        .attr("r", CHART_RADIUS)
        .attr("fill", "none")
        .attr("stroke", "#222")
        .attr("stroke-width", 2);

      // === OUTER SCALES ===
      const scaleGroup = g.append("g").attr("class", "scales");
      const outerRadius1 = CHART_RADIUS + 6;  // Wavelengths toward generator
      const outerRadius2 = CHART_RADIUS + 18; // Wavelengths toward load
      const outerRadius3 = CHART_RADIUS + 30; // Angle scale

      // Draw wavelength scale - Toward Generator (inner, clockwise)
      for (let wl = 0; wl <= 0.5; wl += 0.01) {
        const isMajor = Math.abs(wl % 0.05) < 0.001 || Math.abs(wl % 0.05 - 0.05) < 0.001;
        const isLabel = Math.abs(wl % 0.1) < 0.001;

        const angleRad = -4 * Math.PI * wl; // Clockwise
        const tickLength = isMajor ? 5 : 3;

        const x1 = CENTER + CHART_RADIUS * Math.cos(angleRad);
        const y1 = CENTER - CHART_RADIUS * Math.sin(angleRad);
        const x2 = CENTER + (CHART_RADIUS + tickLength) * Math.cos(angleRad);
        const y2 = CENTER - (CHART_RADIUS + tickLength) * Math.sin(angleRad);

        scaleGroup.append("line")
          .attr("x1", x1)
          .attr("y1", y1)
          .attr("x2", x2)
          .attr("y2", y2)
          .attr("stroke", "#555")
          .attr("stroke-width", isMajor ? 0.8 : 0.4);

        if (isLabel && wl > 0 && wl < 0.5) {
          const labelX = CENTER + (outerRadius1 + 5) * Math.cos(angleRad);
          const labelY = CENTER - (outerRadius1 + 5) * Math.sin(angleRad);

          scaleGroup.append("text")
            .attr("x", labelX)
            .attr("y", labelY)
            .attr("font-size", "7px")
            .attr("fill", "#444")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(wl.toFixed(1));
        }
      }

      // Draw wavelength scale - Toward Load (outer, counter-clockwise)
      for (let wl = 0; wl <= 0.5; wl += 0.01) {
        const isMajor = Math.abs(wl % 0.05) < 0.001 || Math.abs(wl % 0.05 - 0.05) < 0.001;
        const isLabel = Math.abs(wl % 0.1) < 0.001;

        const angleRad = 4 * Math.PI * wl; // Counter-clockwise
        const tickLength = isMajor ? 5 : 3;

        const x1 = CENTER + (outerRadius1 + 1) * Math.cos(angleRad);
        const y1 = CENTER - (outerRadius1 + 1) * Math.sin(angleRad);
        const x2 = CENTER + (outerRadius1 + 1 + tickLength) * Math.cos(angleRad);
        const y2 = CENTER - (outerRadius1 + 1 + tickLength) * Math.sin(angleRad);

        scaleGroup.append("line")
          .attr("x1", x1)
          .attr("y1", y1)
          .attr("x2", x2)
          .attr("y2", y2)
          .attr("stroke", "#777")
          .attr("stroke-width", isMajor ? 0.8 : 0.4);

        if (isLabel && wl > 0 && wl < 0.5) {
          const labelX = CENTER + (outerRadius2 + 3) * Math.cos(angleRad);
          const labelY = CENTER - (outerRadius2 + 3) * Math.sin(angleRad);

          scaleGroup.append("text")
            .attr("x", labelX)
            .attr("y", labelY)
            .attr("font-size", "7px")
            .attr("fill", "#666")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(wl.toFixed(1));
        }
      }

      // Draw angle/phase scale (degrees)
      for (let deg = 0; deg < 360; deg += 10) {
        const isMajor = deg % 30 === 0;
        const isLabel = deg % 30 === 0;

        const angleRad = (deg * Math.PI) / 180;
        const tickLength = isMajor ? 5 : 3;

        const x1 = CENTER + (outerRadius2 + 2) * Math.cos(angleRad);
        const y1 = CENTER - (outerRadius2 + 2) * Math.sin(angleRad);
        const x2 = CENTER + (outerRadius2 + 2 + tickLength) * Math.cos(angleRad);
        const y2 = CENTER - (outerRadius2 + 2 + tickLength) * Math.sin(angleRad);

        scaleGroup.append("line")
          .attr("x1", x1)
          .attr("y1", y1)
          .attr("x2", x2)
          .attr("y2", y2)
          .attr("stroke", "#888")
          .attr("stroke-width", isMajor ? 0.8 : 0.4);

        if (isLabel) {
          let displayDeg = deg;
          if (deg > 180) displayDeg = deg - 360;

          const labelX = CENTER + (outerRadius3 + 5) * Math.cos(angleRad);
          const labelY = CENTER - (outerRadius3 + 5) * Math.sin(angleRad);

          scaleGroup.append("text")
            .attr("x", labelX)
            .attr("y", labelY)
            .attr("font-size", "8px")
            .attr("fill", "#555")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(`${displayDeg}°`);
        }
      }

      // Scale labels/titles
      scaleGroup.append("text")
        .attr("x", CENTER)
        .attr("y", CENTER - outerRadius3 - 20) // Increased padding top
        .attr("font-size", "8px")
        .attr("fill", "#444")
        .attr("text-anchor", "middle")
        .text("WAVELENGTHS TOWARD GENERATOR →");

      scaleGroup.append("text")
        .attr("x", CENTER)
        .attr("y", CENTER + outerRadius3 + 20) // Increased padding bottom
        .attr("font-size", "8px")
        .attr("fill", "#666")
        .attr("text-anchor", "middle")
        .text("← WAVELENGTHS TOWARD LOAD");

      // Add labels for key R values along center line
      const rLabels = [0.2, 0.5, 1, 2, 5];
      rLabels.forEach((r) => {
        const labelX = CENTER + ((2 * r) / (r + 1)) * CHART_RADIUS - 5;
        if (labelX < CENTER + CHART_RADIUS - 10 && labelX > CENTER - CHART_RADIUS + 10) {
          g.append("text")
            .attr("x", labelX)
            .attr("y", CENTER + 11)
            .attr("font-size", "8px")
            .attr("fill", "#444")
            .attr("text-anchor", "middle")
            .text(r.toString());
        }
      });

      // Key point labels
      g.append("text")
        .attr("x", CENTER - CHART_RADIUS - 5)
        .attr("y", CENTER + 3)
        .attr("font-size", "10px")
        .attr("fill", "#222")
        .attr("text-anchor", "end")
        .text("0");

      g.append("text")
        .attr("x", CENTER + CHART_RADIUS + 5)
        .attr("y", CENTER + 3)
        .attr("font-size", "10px")
        .attr("fill", "#222")
        .text("∞");

      // Add X arc labels at perimeter
      const xLabels = [0.2, 0.5, 1, 2];
      xLabels.forEach((xVal) => {
        const angle = Math.atan(xVal);
        const labelDist = CHART_RADIUS - 12;

        // +jX label (inside chart)
        g.append("text")
          .attr("x", CENTER + labelDist * Math.cos(angle))
          .attr("y", CENTER - labelDist * Math.sin(angle))
          .attr("font-size", "8px")
          .attr("fill", "#555")
          .attr("text-anchor", "middle")
          .text(`j${xVal}`);

        // -jX label (inside chart)
        g.append("text")
          .attr("x", CENTER + labelDist * Math.cos(-angle))
          .attr("y", CENTER - labelDist * Math.sin(-angle))
          .attr("font-size", "8px")
          .attr("fill", "#555")
          .attr("text-anchor", "middle")
          .text(`-j${xVal}`);
      });

    }, 500); // Delay until after entrance animation (400ms)

    return () => clearTimeout(timer);
  }, []);

  // Draw the selected point
  useEffect(() => {
    if (!svgRef.current || !point) return;

    const svg = d3.select(svgRef.current);

    svg.selectAll(".smith-point").remove();
    svg.selectAll(".smith-vswr-circle").remove();
    svg.selectAll(".smith-crosshair").remove();

    const { chartX, chartY } = gammaToChart(point.gammaReal, point.gammaImag);

    // Draw VSWR circle
    svg.append("circle")
      .attr("class", "smith-vswr-circle")
      .attr("cx", CENTER)
      .attr("cy", CENTER)
      .attr("r", point.gamma * CHART_RADIUS)
      .attr("fill", "none")
      .attr("stroke", "#3b82f6")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "6,3")
      .attr("opacity", 0.9);

    // Draw crosshair
    const crosshairSize = 10;
    svg.append("line")
      .attr("class", "smith-crosshair")
      .attr("x1", chartX - crosshairSize)
      .attr("y1", chartY)
      .attr("x2", chartX + crosshairSize)
      .attr("y2", chartY)
      .attr("stroke", "#ef4444")
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.7);

    svg.append("line")
      .attr("class", "smith-crosshair")
      .attr("x1", chartX)
      .attr("y1", chartY - crosshairSize)
      .attr("x2", chartX)
      .attr("y2", chartY + crosshairSize)
      .attr("stroke", "#ef4444")
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.7);

    // Draw the point
    svg.append("circle")
      .attr("class", "smith-point")
      .attr("cx", chartX)
      .attr("cy", chartY)
      .attr("r", 6)
      .attr("fill", "#ef4444")
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("cursor", "grab")
      .style("filter", "drop-shadow(0 1px 2px rgba(0,0,0,0.3))");

  }, [point, gammaToChart]);

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    handleChartInteraction(e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      handleChartInteraction(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    handleChartInteraction(touch as unknown as MouseEvent);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      e.preventDefault();
      const touch = e.touches[0];
      handleChartInteraction(touch as unknown as MouseEvent);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div ref={containerRef} className="w-full h-full flex items-center justify-center">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        className="w-full h-full cursor-crosshair select-none"
        preserveAspectRatio="xMidYMid meet"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
}

// Utility functions exported for testing
export const smithChartUtils = {
  impedanceToGamma: (r: number, x: number) => {
    const denomReal = r + 1;
    const denomImag = x;
    const denomMagSq = denomReal * denomReal + denomImag * denomImag;
    const numReal = r - 1;
    const numImag = x;
    const gammaReal = (numReal * denomReal + numImag * denomImag) / denomMagSq;
    const gammaImag = (numImag * denomReal - numReal * denomImag) / denomMagSq;
    return { gammaReal, gammaImag };
  },

  gammaToImpedance: (gammaReal: number, gammaImag: number) => {
    const denomReal = 1 - gammaReal;
    const denomImag = -gammaImag;
    const denomMagSq = denomReal * denomReal + denomImag * denomImag;
    if (denomMagSq < 0.0001) {
      return { r: Infinity, x: 0 };
    }
    const numReal = 1 + gammaReal;
    const numImag = gammaImag;
    const r = (numReal * denomReal + numImag * denomImag) / denomMagSq;
    const x = (numImag * denomReal - numReal * denomImag) / denomMagSq;
    return { r: Math.max(0, r), x };
  },

  calculateVSWR: (gamma: number) => {
    return gamma < 0.9999 ? (1 + gamma) / (1 - gamma) : Infinity;
  },

  calculateReturnLoss: (gamma: number) => {
    return gamma > 0 ? -20 * Math.log10(gamma) : Infinity;
  },

  calculateMismatchLoss: (gamma: number) => {
    return -10 * Math.log10(1 - gamma * gamma);
  }
};
